if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.section = {
          settings: JSON.parse(this.getAttribute('data-section-settings') || '{}')
        };
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          dots: this.querySelector('.mobile-dot-navigation'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.productInfo = document.getElementById(`ProductInfo-${this.dataset.section}`);
        this.prependMedia = this.dataset.disablePrepend != 'true';
        if (this.productInfo && Shopify.postLinksRetry) this.productInfo.initShareLinks();
        this.filteringOption = this.dataset.filteringOption;
        this.productSpecificFiltering = this.dataset.productSpecificFiltering === 'true';
        this.comboGroups = null;
        try {
          const rawCombo = this.getAttribute('data-combo-groups');
          if (rawCombo) this.comboGroups = JSON.parse(rawCombo);
        } catch (e) {
          this.comboGroups = null;
        }
        this.videoIntersectionObserver = null;
        this.videoObserverTarget = null;
        this.videoObservers = [];

        const isGridLayout = ['columns', 'columns_1', 'stacked'].includes(this.dataset.desktopLayout || '');

        if (this.elements.viewer) {
          if (isGridLayout) {
            this.initVideoAutoplayForGridLayout();
          } else {
            this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
            const initialActiveMedia = this.elements.viewer.querySelector('.product__media-item.is-active');
            if (initialActiveMedia) this.playActiveMedia(initialActiveMedia);
          }
        }

        // Add thumbnail event listeners
        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', (event) => {
                // First set the active media
                this.setActiveMedia(mediaToSwitch.dataset.target, false);
                // Then handle thumbnail scrolling behavior
                this.handleThumbnailClick(event, mediaToSwitch);
              });
          });
        }

        // Add dot navigation event listeners
        if (this.elements.dots) {
          this.elements.dots.querySelectorAll('[data-target]').forEach((dot) => {
            dot.addEventListener('click', () => {
              this.setActiveMedia(dot.dataset.target, false);
            });
          });
        }
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        // Listen for variant changes if filtering is enabled
        if (this.filteringOption) {
          document.addEventListener('variant:change', this.handleVariantChange.bind(this));
          // Initial stacked layout fix: ensure first visible gets full width when filtering may hide first child
          if (this.dataset.desktopLayout === 'stacked' && this.elements.viewer) {
            this.updateStackedLayoutFirstVisible();
          }
        }

        // Play overlay click (when autoplay disabled)
        this.addEventListener('click', this.handleVideoPlayOverlayClick.bind(this));
      }

      handleVideoPlayOverlayClick(event) {
        const overlay = event.target.closest('.product-media-video-play-overlay');
        if (!overlay) return;
        const wrapper = overlay.closest('.product-media-video-wrapper');
        const video = wrapper?.querySelector('video');
        if (video) {
          if (!overlay.dataset.listenersAdded) {
            video.addEventListener('pause', () => overlay.classList.remove('hidden'));
            video.addEventListener('ended', () => overlay.classList.remove('hidden'));
            overlay.dataset.listenersAdded = 'true';
          }
          video.play().catch(() => {});
          overlay.classList.add('hidden');
        }
      }

      onSlideChanged(event) {
        // Update active media and play when slide changes (e.g. via slider scroll)
        const currentElement = event.detail?.currentElement;
        if (currentElement) {
          this.elements.viewer.querySelectorAll('.product__media-item').forEach((el) => el.classList.remove('is-active'));
          currentElement.classList.add('is-active');
          this.playActiveMedia(currentElement);
        }

        // Update thumbnail navigation
        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          if (thumbnail && !thumbnail.classList.contains('hidden')) {
            this.setActiveThumbnail(thumbnail);
          }
        }

        // Update dot navigation
        if (this.elements.dots) {
          this.setActiveDot(event.detail.currentElement.dataset.mediaId);
        }
      }

      setActiveMedia(mediaId, prepend, filtering = false, currentVariant = null) {
        if (filtering && currentVariant) {
          // Use the dedicated filtering method
          this.updateMediaFiltering(currentVariant);
        }

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend && this.prependMedia) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          if (!this.section?.settings?.no_scroll) {
            window.scrollTo({ top: top, behavior: 'smooth' });
          }
        });
        const isGridLayout = ['columns', 'columns_1', 'stacked'].includes(this.dataset.desktopLayout || '');
        if (!isGridLayout) this.playActiveMedia(activeMedia);

        if (filtering && currentVariant) {
          if (this.elements.viewer && this.elements.viewer.initPages) this.elements.viewer.initPages();
          if (this.elements.viewer && this.elements.viewer.update) this.elements.viewer.update();

          // Update thumbnail navigation after filtering
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail && !activeThumbnail.classList.contains('hidden')) {
              this.setActiveThumbnail(activeThumbnail);
              this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }
          }

          // Update dot navigation after filtering
          if (this.elements.dots) {
            this.setActiveDot(mediaId);
          }

          return; // Skip the regular navigation update when filtering
        }

        if (!this.elements.thumbnails && !this.elements.dots) return;

        // Update thumbnail navigation (regular flow)
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        }

        // Update dot navigation (regular flow)
        if (this.elements.dots) {
          this.setActiveDot(mediaId);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        // Check if thumbnail is visible, if not, scroll to make it visible
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        // Get thumbnail position relative to the slider
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const sliderRect = this.elements.thumbnails.slider.getBoundingClientRect();

        // Calculate if thumbnail is on the left or right side of the visible area
        const isLeftSide = thumbnailRect.left < sliderRect.left + (sliderRect.width * 0.3);
        const isRightSide = thumbnailRect.right > sliderRect.right - (sliderRect.width * 0.3);

        if (isLeftSide) {
          // Scroll to show previous thumbnails (scroll left)
          const scrollAmount = Math.max(0, this.elements.thumbnails.slider.scrollLeft - (this.elements.thumbnails.sliderItemOffset * 2));
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else if (isRightSide) {
          // Scroll to show next thumbnails (scroll right)
          const scrollAmount = this.elements.thumbnails.slider.scrollLeft + (this.elements.thumbnails.sliderItemOffset * 2);
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else {
          // Default behavior - scroll to make thumbnail visible
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
        }
      }

      setActiveDot(mediaId) {
        if (!this.elements.dots) return;

        this.elements.dots
          .querySelectorAll('.dot-navigation-dot')
          .forEach((dot) => {
            dot.classList.remove('active');
            dot.removeAttribute('aria-current');
          });

        const activeDot = this.elements.dots.querySelector(`[data-target="${mediaId}"]`);
        if (activeDot) {
          activeDot.classList.add('active');
          activeDot.setAttribute('aria-current', true);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      initVideoAutoplayForGridLayout() {
        const enableAutoplay = this.section?.settings?.enable_video_autoplay !== false;
        if (!enableAutoplay || !this.elements.viewer) return;

        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach((item) => {
          const deferredMedia = item.querySelector('.deferred-media');
          const nativeVideo = item.querySelector('video');
          if (!deferredMedia && !nativeVideo) return;

          const playMedia = () => {
            if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
              deferredMedia.loadContent(false);
            } else if (deferredMedia) {
              const video = deferredMedia.querySelector('video');
              const iframe = deferredMedia.querySelector('iframe');
              if (video) video.play().catch(() => {});
              if (iframe?.contentWindow) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"play"}', '*');
                }
              }
            }
            if (nativeVideo) nativeVideo.play().catch(() => {});
          };

          const pauseMedia = () => {
            if (deferredMedia) {
              const video = deferredMedia.querySelector('video');
              const iframe = deferredMedia.querySelector('iframe');
              if (video) video.pause();
              if (iframe?.contentWindow) {
                if (iframe.classList.contains('js-youtube')) {
                  iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
                } else if (iframe.classList.contains('js-vimeo')) {
                  iframe.contentWindow.postMessage('{"method":"pause"}', '*');
                }
              }
            }
            if (nativeVideo) nativeVideo.pause();
          };

          const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                playMedia();
              } else {
                pauseMedia();
              }
            });
          }, { threshold: 0.25, rootMargin: '0px' });

          observer.observe(item);
          this.videoObservers.push({ observer, target: item });
        });
      }

      pauseAllGalleryMedia() {
        const gallery = this;
        gallery.querySelectorAll('video').forEach((v) => v.pause());
        gallery.querySelectorAll('.js-youtube').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        });
        gallery.querySelectorAll('.js-vimeo').forEach((iframe) => {
          iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
        });
      }

      playActiveMedia(activeItem) {
        // Pause all media in this gallery first (including previous slide)
        this.pauseAllGalleryMedia();

        // Disconnect previous observer and explicitly pause its media
        if (this.videoIntersectionObserver && this.videoObserverTarget) {
          this.videoIntersectionObserver.unobserve(this.videoObserverTarget);
          this.videoObserverTarget = null;
        }

        const enableAutoplay = this.section?.settings?.enable_video_autoplay !== false;
        const deferredMedia = activeItem.querySelector('.deferred-media');
        const nativeVideo = activeItem.querySelector('video');

        if (!enableAutoplay) {
          if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
            deferredMedia.loadContent(false);
          }
          return;
        }

        const playMedia = () => {
          if (deferredMedia && !deferredMedia.hasAttribute('loaded')) {
            deferredMedia.loadContent(false);
          } else if (deferredMedia) {
            const video = deferredMedia.querySelector('video');
            const iframe = deferredMedia.querySelector('iframe');
            if (video) video.play().catch(() => {});
            if (iframe?.contentWindow) {
              if (iframe.classList.contains('js-youtube')) {
                iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
              } else if (iframe.classList.contains('js-vimeo')) {
                iframe.contentWindow.postMessage('{"method":"play"}', '*');
              }
            }
          }
          if (nativeVideo) {
            nativeVideo.play().catch(() => {});
          }
        };

        const pauseMedia = () => {
          if (deferredMedia) {
            const video = deferredMedia.querySelector('video');
            if (video) video.pause();
          }
          if (nativeVideo) nativeVideo.pause();
        };

        const observerCallback = (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              playMedia();
            } else {
              pauseMedia();
            }
          });
        };

        this.playMediaGeneration = (this.playMediaGeneration || 0) + 1;
        const currentGeneration = this.playMediaGeneration;

        const observerCallbackWithGuard = (entries) => {
          if (currentGeneration !== this.playMediaGeneration) return;
          observerCallback(entries);
        };

        this.videoIntersectionObserver = new IntersectionObserver(observerCallbackWithGuard, {
          threshold: 0.25,
          rootMargin: '0px'
        });
        this.videoObserverTarget = activeItem;
        this.videoIntersectionObserver.observe(activeItem);

        // Play immediately if already in view
        const rect = activeItem.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight && rect.bottom > 0;
        if (isInView) playMedia();
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      handleThumbnailClick(event, thumbnail) {
        if (!this.elements.thumbnails || !this.elements.thumbnails.slider) return;

        // Get thumbnail position relative to the slider
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const sliderRect = this.elements.thumbnails.slider.getBoundingClientRect();

        // Calculate if thumbnail is on the left or right side of the visible area
        const isLeftSide = thumbnailRect.left < sliderRect.left + (sliderRect.width * 0.3);
        const isRightSide = thumbnailRect.right > sliderRect.right - (sliderRect.width * 0.3);

        if (isLeftSide) {
          // Scroll to show previous thumbnails (scroll left)
          const scrollAmount = Math.max(0, this.elements.thumbnails.slider.scrollLeft - (this.elements.thumbnails.sliderItemOffset * 2));
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        } else if (isRightSide) {
          // Scroll to show next thumbnails (scroll right)
          const scrollAmount = this.elements.thumbnails.slider.scrollLeft + (this.elements.thumbnails.sliderItemOffset * 2);
          this.elements.thumbnails.slider.scrollTo({ left: scrollAmount, behavior: 'smooth' });
        }
      }

      handleVariantChange(event) {
        if (!this.filteringOption || !event.detail.variant) return;

        // Get the current variant from the event
        const currentVariant = event.detail.variant;

        // Update the filtering based on the new variant
        this.updateMediaFiltering(currentVariant);

        // Find the first visible media item and set it as active
        const firstVisibleMedia = this.querySelector('.product__media-item:not(.hidden)');
        if (firstVisibleMedia) {
          const mediaId = firstVisibleMedia.dataset.mediaId;
          this.setActiveMedia(mediaId, false, true, currentVariant);
        }
      }

      updateStackedLayoutFirstVisible() {
        const isStackedLayout = this.dataset.desktopLayout === 'stacked';
        if (!isStackedLayout || !this.elements.viewer) return;
        const mediaItems = this.elements.viewer.querySelectorAll('.product__media-item');
        mediaItems.forEach(item => item.classList.remove('product__media-item--full'));
        const firstVisible = this.elements.viewer.querySelector('.product__media-item:not(.hidden)');
        if (firstVisible) {
          firstVisible.classList.add('product__media-item--full');
        }
      }

      updateMediaFiltering(currentVariant) {
        if (!this.filteringOption || !currentVariant) return;

        // Determine if compound (Best Seller + Color) filtering applies for this product.
        // Only kicks in when a "gallery_groups" metafield-derived mapping was rendered onto
        // this element (data-combo-groups) AND the current variant's combo key exists in it.
        // Falls back to the original single-option alt-text filtering otherwise, so every
        // other product/theme behavior stays 100% unchanged.
        let comboAllowedIds = null;
        if (this.comboGroups) {
          const comboKey = `${currentVariant.option1}|${currentVariant.option2}`;
          const group = this.comboGroups[comboKey];
          if (group && group.length) {
            comboAllowedIds = group.map(String);
          }
        }

        // Filter all media items, thumbnails, and dots
        const allSlides = this.querySelectorAll('.product__media-item, .thumbnail-list__item, .dot-navigation-dot');
        allSlides.forEach(slide => {
          if (comboAllowedIds) {
            const idAttr = slide.dataset.mediaId || slide.dataset.target || '';
            const rawId = idAttr.split('-').pop();
            const shouldShow = comboAllowedIds.includes(rawId);
            if (shouldShow) {
              slide.classList.remove('hidden');
            } else {
              slide.classList.add('hidden');
            }
            return;
          }

          const slideAlt = slide.dataset.alt;
          // Treat images without alt tags as 'always_display'
          const shouldShow = slideAlt === currentVariant[this.filteringOption] ||
                           slideAlt === 'always_display' ||
                           !slideAlt ||
                           slideAlt === '';

          if (shouldShow) {
            slide.classList.remove('hidden');
          } else {
            slide.classList.add('hidden');
          }
        });

        // Stacked layout: ensure first visible media item gets full width (fixes layout when first child is hidden)
        this.updateStackedLayoutFirstVisible();

        // Also filter slider counter links if they exist (combo mode has no per-link id, so keep alt-based here)
        const sliderLinks = this.querySelectorAll('.slider-counter__link');
        sliderLinks.forEach(link => {
          const linkAlt = link.dataset.alt;
          // Treat links without alt tags as 'always_display'
          const shouldShow = linkAlt === currentVariant[this.filteringOption] ||
                           linkAlt === 'always_display' ||
                           !linkAlt ||
                           linkAlt === '';

          if (shouldShow) {
            link.classList.remove('hidden');
          } else {
            link.classList.add('hidden');
          }
        });
      }
    }
  );
}
